import { AssistantToolName, AssistantUIMessage } from "@repo/api/client";
import {
	eligibilityPayorsForTenant,
	payorLabelsForStaff,
} from "@repo/config/tenants";
import { Button, Flex, Icon, Text, ThemeOverride } from "@repo/ui";
import { useStableCallback } from "@repo/utils/hooks/useStableCallback";
import { TextUIPart, isToolUIPart, type ChatStatus } from "ai";
import { useMemo, useRef, useState } from "react";
import {
	useAssistantChat,
	type ConfirmToolCallFn,
} from "../../ai/useAssistantChat";
import { useEntity } from "../../db/DatabaseProvider";
import { TaskId } from "../../db/schema";
import { parseMarkdown } from "../../markdown/parser";
import { getTenantSlug } from "../../tenant";
import { formatError } from "../../utils/errorUtils";
import { CollapsibleDetails } from "../CollapsibleDetails";
import { ContextChip, ContextChipView } from "../ContextChip";
import { useMeasure } from "../../utils/useMeasure";
import { TextEditor, type TextEditorRef } from "../editor/TextEditor";
import { MarkdownDocView } from "../MarkdownDocView";
import { CallProviderToolCallView } from "./tools/CallProviderTool";
import { SendMessageToPatientToolCallView } from "./tools/SendMessageToPatientTool";
import { ToolCallView } from "./tools/ToolCallView";
import { ToolConfirmationCard } from "./tools/ToolConfirmationCard";
// import { UpdateProviderInfoToolCallView } from "./tools/UpdateProviderInfoTool";

type AssistantUIMessagePart = AssistantUIMessage["parts"][number];

// MessagePart re-renders on every streamed chunk, so a plain console.warn
// would repeat hundreds of times for one skipped part.
const warnedMessages = new Set<string>();
function warnOnce(message: string) {
	if (warnedMessages.has(message)) return;
	warnedMessages.add(message);
	console.warn(message);
}

const TextPart: React.FC<{
	part: TextUIPart;
	role: "user" | "assistant";
}> = ({ part, role }) => {
	const markdown = useMemo(() => {
		return parseMarkdown(part.text);
	}, [part.text]);

	return (
		<Flex
			paddingHorizontal="sm"
			borderRadius="xl"
			background={role === "user" ? "accent9" : undefined}
		>
			<ThemeOverride mode={role === "user" ? "dark" : undefined}>
				<MarkdownDocView doc={markdown} size="prose-sm" />
			</ThemeOverride>
		</Flex>
	);
};

function MessagePart({
	part,
	role,
	pendingToolCalls,
	confirmToolCall,
	taskId,
}: {
	part: AssistantUIMessagePart;
	role: "user" | "assistant";
	pendingToolCalls: Array<{
		toolName: AssistantToolName;
		toolCallId: string;
		input: any;
	}>;
	confirmToolCall: ConfirmToolCallFn;
	taskId?: TaskId;
}) {
	switch (part.type) {
		// Sonnet 5.5 can't turn thinking off, so the stream carries reasoning
		// parts (often with empty text). They aren't shown to staff, but they
		// must stay in `messages`: after a confirmed tool call the server needs
		// that turn's thinking block back, signature and all.
		case "step-start":
		case "reasoning":
			return undefined;
		case "text":
			return <TextPart part={part} role={role} />;

		case "tool-send_message_to_patient":
			return (
				<SendMessageToPatientToolCallView
					part={part}
					isPendingConfirmation={pendingToolCalls.some(
						(tc) => tc.toolCallId === part.toolCallId,
					)}
					confirmToolCall={confirmToolCall}
					taskId={taskId}
				/>
			);
		case "tool-call_provider":
			return (
				<CallProviderToolCallView
					part={part}
					isPendingConfirmation={pendingToolCalls.some(
						(tc) => tc.toolCallId === part.toolCallId,
					)}
					confirmToolCall={confirmToolCall}
				/>
			);
		// case "tool-update_provider_info":
		// 	return (
		// 		<UpdateProviderInfoToolCallView
		// 			part={part}
		// 			isPendingConfirmation={pendingToolCalls.some(
		// 				(tc) => tc.toolCallId === part.toolCallId,
		// 			)}
		// 			confirmToolCall={confirmToolCall}
		// 		/>
		// 	);
		case "tool-attach_records_send_referral":
		case "tool-check_patient_eligibility":
		case "tool-submit_referral_prior_authorization":
			return (
				<ToolConfirmationCard
					part={part}
					isPendingConfirmation={pendingToolCalls.some(
						(tc) => tc.toolCallId === part.toolCallId,
					)}
					confirmToolCall={confirmToolCall}
				/>
			);
		case "tool-search_healthcare_providers":
			return <ToolCallView part={part} />;

		default:
			// Throwing here unmounts the whole app (there is no error boundary),
			// so skip part types this pane doesn't render yet.
			warnOnce(`Assistant pane skipped unknown part type: ${part.type}`);
			return undefined;
	}
}

type AssistantMessageInputProps = {
	inputRef: React.RefObject<TextEditorRef>;
	placeholder: string;
	onSubmit: () => Promise<void>;
	status: string;
	disabled?: boolean;
	chips?: ContextChip[];
	autoFocus?: boolean;
	onRemoveChip?: (chip: ContextChip) => void;
	onChange?: (value: string) => void;
};

function AssistantMessageInput({
	inputRef,
	placeholder,
	onSubmit,
	status,
	disabled,
	chips = [],
	autoFocus,
	onRemoveChip,
	onChange,
}: AssistantMessageInputProps) {
	return (
		<Flex
			flex
			column
			gap="xs"
			padding="xs"
			background="background"
			borderRadius="md"
			shadow="subtle"
		>
			{/* Context Chips */}
			{chips.length > 0 && (
				<Flex gap="xs" wrap>
					{chips.map((chip) => (
						<ContextChipView
							key={chip.id}
							chip={chip}
							onClose={() => onRemoveChip?.(chip)}
						/>
					))}
				</Flex>
			)}
			{/* Input */}
			<Flex gap="md" wrap align="end" justify="end">
				<TextEditor
					ref={inputRef}
					autoFocus={autoFocus}
					aria-label="Assistant message input"
					placeholder={placeholder}
					onSubmit={onSubmit}
					onChange={onChange}
					size="prose-sm"
					style={{ flex: 1 }}
				/>
				<Flex justify="end">
					<Button
						size="sm"
						variant="ghost"
						onPress={onSubmit}
						aria-label="Send message to assistant"
						style={{
							opacity: status === "streaming" || disabled ? 0.5 : 1,
							pointerEvents:
								status === "streaming" || disabled ? "none" : "auto",
						}}
					>
						<Icon name="ArrowUp" size="sm" />
					</Button>
				</Flex>
			</Flex>
		</Flex>
	);
}

type EmptyStateProps = {
	inputRef: React.RefObject<TextEditorRef>;
	placeholder: string;
	onSubmit: () => Promise<void>;
	status: string;
	chips: ContextChip[];
	onRemoveChip: (chip: ContextChip) => void;
	onChange: (value: string) => void;
	currentInputValue: string;
	handleCapabilityClick: (text: string) => void;
};

/**
 * The payors this tenant can actually check, as clinic staff would say them.
 * Was a hardcoded Texas list shown to every tenant, and it had already fallen
 * out of step with the gate it describes.
 */
function supportedInsurancesLabel(): string {
	const tenantSlug = getTenantSlug();
	if (!tenantSlug) return "";
	return payorLabelsForStaff(eligibilityPayorsForTenant(tenantSlug)).join(", ");
}

function EmptyState({
	inputRef,
	placeholder,
	onSubmit,
	status,
	chips,
	onRemoveChip,
	onChange,
	currentInputValue,
	handleCapabilityClick,
}: EmptyStateProps) {
	return (
		<Flex column flex={1} justify="space-between" padding="lg" gap="lg">
			{/* Top section: Input and capability buttons */}
			<Flex column gap="lg">
				{/* Input and chips at the top */}
				<AssistantMessageInput
					inputRef={inputRef}
					placeholder={placeholder}
					onSubmit={onSubmit}
					status={status}
					chips={chips}
					onRemoveChip={onRemoveChip}
					onChange={onChange}
				/>

				{/* Capability buttons */}
				{!currentInputValue && (
					<Flex column align="end" gap="md">
						<Button
							variant="ghost"
							onPress={() =>
								handleCapabilityClick("Check patient insurance eligibility")
							}
							size="sm"
							icon="Shield"
							color="sand11"
							style={{ whiteSpace: "nowrap" }}
						>
							Check insurance eligibility
						</Button>
						<Button
							variant="ghost"
							onPress={() =>
								handleCapabilityClick("In Spruce, send the patient a message ")
							}
							size="sm"
							icon="MessageSquare"
							color="sand11"
							style={{ whiteSpace: "nowrap" }}
						>
							Send message to patient
						</Button>
						<Button
							variant="ghost"
							onPress={() =>
								handleCapabilityClick("Search providers to find me ")
							}
							size="sm"
							icon="Search"
							color="sand11"
							style={{ whiteSpace: "nowrap" }}
						>
							Search for healthcare providers
						</Button>
						{/* <Button
							variant="ghost"
							onPress={() =>
								handleCapabilityClick("Update provider information for ")
							}
							size="sm"
							icon="UserPen"
							color="sand11"
							style={{ whiteSpace: "nowrap" }}
						>
							Update provider information
						</Button> */}
						<Button
							variant="ghost"
							onPress={() => handleCapabilityClick("Call ")}
							size="sm"
							icon="Phone"
							color="sand11"
							style={{ whiteSpace: "nowrap" }}
						>
							Call a provider's office
						</Button>
						<Button
							variant="ghost"
							onPress={() =>
								handleCapabilityClick(
									"Submit a prior authorization for the referral",
								)
							}
							size="sm"
							icon="ClipboardCheck"
							color="sand11"
							style={{ whiteSpace: "nowrap" }}
						>
							Submit referral prior authorization
						</Button>
						<Button
							variant="ghost"
							onPress={() =>
								handleCapabilityClick("Send the patient's referral")
							}
							size="sm"
							icon="Send"
							color="sand11"
							style={{ whiteSpace: "nowrap" }}
						>
							Send referral with records
						</Button>
					</Flex>
				)}
			</Flex>

			{/* Bottom section: Insurance text */}
			<Flex column padding="xxl" justify="center">
				<Text size="sm" align="center" balance color="sand9">
					Supported insurances: {supportedInsurancesLabel()}
				</Text>
			</Flex>
		</Flex>
	);
}

/**
 * True from send until the reply's current step shows something on screen.
 * Reasoning parts are hidden, and Sonnet 5.5 rarely opens a tool call with a
 * sentence, so without an indicator the pane looks idle while the model works.
 */
function isAwaitingVisibleReply(
	status: ChatStatus,
	messages: AssistantUIMessage[],
): boolean {
	if (status === "submitted") return true;
	if (status !== "streaming") return false;

	const lastMessage = messages[messages.length - 1];
	if (lastMessage?.role !== "assistant") return true;

	// After a confirmed tool call the reply continues the same message as a new
	// step, so only parts since the last step-start count.
	const stepStart = lastMessage.parts.findLastIndex(
		(part) => part.type === "step-start",
	);
	return !lastMessage.parts
		.slice(stepStart + 1)
		.some(
			(part) =>
				(part.type === "text" && part.text.trim() !== "") || isToolUIPart(part),
		);
}

type AssistantPaneProps = {
	taskId: TaskId;
};

export function AssistantPane({ taskId }: AssistantPaneProps) {
	const inputRef = useRef<TextEditorRef>(null);

	const task = useEntity("task", taskId);

	const defaultContextChips: ContextChip[] = useMemo(() => {
		if (!task) return [];

		return [
			{ type: "task", id: task.id },
			{ type: "patient", id: task.patient },
		];
	}, [task]);

	const [overrideChips, setOverrideChips] = useState<ContextChip[] | undefined>(
		undefined,
	);
	const [currentInputValue, setCurrentInputValue] = useState<string>("");
	const [bottomAreaRef, { height }] = useMeasure();

	const chips = overrideChips ?? defaultContextChips;

	const {
		messages,
		sendMessage,
		status,
		error,
		confirmToolCall,
		pendingToolCalls,
		startNewChat,
	} = useAssistantChat({
		taskId,
		chips,
	});

	// Check if the assistant is ready to accept new messages
	// Block when: assistant is streaming/busy OR there are pending tool confirmations OR last message hasn't been fully processed
	const canSendMessage = useMemo(() => {
		// Block if assistant is not in ready state
		if (status !== "ready") return false;

		// Block if there are any pending tool confirmations
		if (pendingToolCalls.length > 0) return false;

		// Allow if no messages yet
		if (messages.length === 0) return true;

		const lastMessage = messages[messages.length - 1];
		if (!lastMessage) return true;

		// If last message is from user, assistant needs to respond first
		if (lastMessage.role === "user") return false;

		// If last message is from assistant, check if all tool calls are complete
		if (lastMessage.role === "assistant") {
			// output-error is finished too: the tool never ran, and the SDK has
			// already sent the error back to the model. Counting it as incomplete
			// would disable the input for the rest of the chat.
			const hasIncompleteToolCall = lastMessage.parts.some(
				(part) =>
					isToolUIPart(part) &&
					part.state !== "output-available" &&
					part.state !== "output-error",
			);

			return !hasIncompleteToolCall;
		}

		// For other roles (system, etc.), allow sending
		return true;
	}, [status, pendingToolCalls.length, messages]);

	const onSubmit = async () => {
		const input = inputRef.current?.getValue() || "";
		if (!input.trim() || !canSendMessage) return;

		inputRef.current?.clear();
		setCurrentInputValue("");
		await sendMessage(input);
	};

	const handleCapabilityClick = useStableCallback((text: string) => {
		inputRef.current?.focus();
		inputRef.current?.insertText(text);
	});

	const handleInputChange = useStableCallback((value: string) => {
		setCurrentInputValue(value);
	});

	const onClear = async () => {
		await startNewChat();
		setCurrentInputValue("");
		setOverrideChips(undefined);
	};

	const onRemoveChip = useStableCallback((chipToRemove: ContextChip) => {
		const currentChips = overrideChips ?? defaultContextChips;
		const updatedChips = currentChips.filter(
			(chip) =>
				!(chip.type === chipToRemove.type && chip.id === chipToRemove.id),
		);
		setOverrideChips(updatedChips);
	});

	const isEmpty = messages.length === 0;
	const placeholder = isEmpty
		? "Ask about this task or patient"
		: "Ask a follow-up";

	return (
		<Flex
			column
			flex={1}
			role="complementary"
			overflow="hidden"
			aria-label="AI Assistant"
			style={{ position: "relative" }}
		>
			{/* Header - Sticky */}
			<Flex
				padding="lg"
				borderBottomWidth="md"
				borderColor="sandA3"
				align="center"
				justify="space-between"
			>
				<Text
					size="lg"
					weight="medium"
					color="sand12"
					role="heading"
					aria-level={2}
				>
					AI Assistant
				</Text>
				<Button
					size="sm"
					variant="ghost"
					onPress={onClear}
					aria-label="Clear conversation"
				>
					<Icon name="SquarePen" size="sm" />
				</Button>
			</Flex>

			{/* Scrollable chat area */}
			<Flex column flex={1} overflow="y" style={{ paddingBottom: height ?? 0 }}>
				{isEmpty ? (
					<EmptyState
						inputRef={inputRef}
						placeholder={placeholder}
						onSubmit={onSubmit}
						status={status}
						chips={chips}
						onRemoveChip={onRemoveChip}
						onChange={handleInputChange}
						currentInputValue={currentInputValue}
						handleCapabilityClick={handleCapabilityClick}
					/>
				) : (
					<Flex column flex={1} padding="lg" gap="lg">
						{messages.map((message, index: number) => (
							<Flex
								key={index}
								column
								gap="xs"
								align={message.role === "user" ? "flex-end" : "stretch"}
							>
								<Flex
									column
									style={{ maxWidth: message.role === "user" ? "80%" : "100%" }}
								>
									<Flex column gap="xs">
										{message.parts.map((part, partIndex) =>
											// Ignore system messages
											message.role !== "system" ? (
												<MessagePart
													key={partIndex}
													part={part}
													role={message.role}
													pendingToolCalls={pendingToolCalls}
													confirmToolCall={confirmToolCall}
													taskId={taskId}
												/>
											) : undefined,
										)}
									</Flex>
								</Flex>
							</Flex>
						))}

						{isAwaitingVisibleReply(status, messages) && (
							<Text size="sm" color="sand9" shimmer>
								Working…
							</Text>
						)}

						{error && (
							<Flex column gap="xs" align="flex-start">
								<Flex column align="flex-start" gap="xs">
									<Flex
										padding="xs"
										borderRadius="md"
										background="red3"
										borderWidth="md"
										borderColor="red6"
									>
										<Text size="md" color="red11">
											Something went wrong
										</Text>
									</Flex>
									<Text size="md" color="sand12" weight="bold" italic>
										Please contact Skyway support for assistance with this
										issue.
									</Text>
									<CollapsibleDetails
										label="Show details"
										content={formatError(error)}
									/>
								</Flex>
							</Flex>
						)}
					</Flex>
				)}
				{/* Input area - Sticky at bottom for active conversations */}
				{!isEmpty && (
					<Flex
						ref={bottomAreaRef}
						padding="md"
						style={{ position: "absolute", bottom: 0, left: 0, right: 0 }}
					>
						<AssistantMessageInput
							autoFocus
							inputRef={inputRef}
							placeholder={placeholder}
							onSubmit={onSubmit}
							status={status}
							disabled={!canSendMessage}
							chips={[]}
							onRemoveChip={onRemoveChip}
							onChange={handleInputChange}
						/>
					</Flex>
				)}
			</Flex>
		</Flex>
	);
}
